import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import BackToTop from '@/components/BackToTop';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts/${slug}`).then(r => setPost(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
        <Navigation />
        <div className="max-w-3xl mx-auto px-4 pt-36 text-center">
          <h1 className="font-['Outfit'] text-3xl font-bold text-[#0A0A0A] dark:text-white mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#00B4D8] hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "url": `https://uidesigns.co.uk/blog/${post.slug}`,
    "datePublished": post.published_at,
    "author": { "@type": "Person", "name": "UI Designs" },
    "publisher": { "@type": "Organization", "name": "UI Designs" }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <SEOHead
        title={`${post.title} | UI Designs Blog`}
        description={post.excerpt}
        canonical={`https://uidesigns.co.uk/blog/${post.slug}`}
        keywords={post.keywords || ''}
        schema={schema}
      />
      <Navigation />

      <main>
        <article className="relative pt-28 md:pt-36 pb-24 md:pb-32">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#00E5FF] transition-colors mb-8" data-testid="blog-back">
                <ArrowLeft className="w-4 h-4" /> Back to Blog
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              {post.tag && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#00B4D8] mb-3 block">{post.tag}</span>
              )}
              <h1 data-testid="blog-post-title" className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0A0A0A] dark:text-white mb-6 leading-tight">
                {post.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-slate-400 mb-10">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                {post.read_time && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {post.read_time} min read
                  </span>
                )}
              </div>
            </ScrollReveal>

            {post.cover_image && (
              <ScrollReveal delay={150}>
                <div className="rounded-2xl overflow-hidden mb-10 border border-slate-200 dark:border-white/10">
                  <img
                    src={post.cover_image}
                    alt={`${post.title} — web design Bournemouth`}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
              </ScrollReveal>
            )}

            <ScrollReveal delay={200}>
              <div
                data-testid="blog-post-content"
                className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-['Outfit'] prose-headings:tracking-tight prose-a:text-[#00B4D8] prose-a:no-underline hover:prose-a:underline prose-strong:text-[#0A0A0A] dark:prose-strong:text-white prose-p:leading-relaxed prose-li:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </ScrollReveal>

            {/* Cross-links */}
            <div className="mt-16 pt-10 border-t border-slate-200 dark:border-white/10">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Related services</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/shop" className="px-4 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Custom Print Shop</Link>
                <Link to="/web-design-bournemouth" className="px-4 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Web Design Bournemouth</Link>
                <Link to="/web-design-barbers-bournemouth" className="px-4 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Barber Websites</Link>
                <Link to="/web-design-gyms-bournemouth" className="px-4 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Gym Websites</Link>
                <Link to="/web-design-restaurants-bournemouth" className="px-4 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Restaurant Websites</Link>
                <Link to="/blog" className="px-4 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">More Articles</Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <CookieConsent />
      <BackToTop />
    </div>
  );
}
